import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { AuthContext, API } from '@/App';
import { toast } from 'sonner';
import { Shield, Eye, EyeOff, Save, RefreshCw, AlertCircle, CheckCircle2 } from 'lucide-react';

const ReCaptchaSettings = () => {
  const { user } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showSecretKey, setShowSecretKey] = useState(false);
  const [settings, setSettings] = useState({
    site_key: '',
    secret_key: '',
    enabled: true
  });

  useEffect(() => {
    if (user && user.role === 'superadmin') {
      fetchSettings();
    }
  }, [user]);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/superadmin/settings/recaptcha`);
      setSettings(response.data);
    } catch (error) {
      console.error('Error fetching reCAPTCHA settings:', error);
      toast.error('Fehler beim Laden der reCAPTCHA-Einstellungen');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await axios.put(`${API}/superadmin/settings/recaptcha`, settings);
      toast.success('reCAPTCHA-Einstellungen erfolgreich gespeichert');
    } catch (error) {
      console.error('Error saving reCAPTCHA settings:', error);
      toast.error('Fehler beim Speichern der reCAPTCHA-Einstellungen');
    } finally {
      setSaving(false);
    }
  };

  if (!user || user.role !== 'superadmin') {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-800">Zugriff verweigert</h2>
          <p className="text-gray-600 mt-2">Nur SuperAdmin kann diese Seite aufrufen.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-lg shadow-lg p-6 mb-6">
        <div className="flex items-center gap-3">
          <Shield className="w-10 h-10 text-white" />
          <div>
            <h1 className="text-3xl font-bold text-white">Google reCAPTCHA v2 Einstellungen</h1>
            <p className="text-blue-100 mt-1">Verwalten Sie die reCAPTCHA-Konfiguration für die Registrierung</p>
          </div>
        </div>
      </div>

      {/* Instructions Card */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6">
        <h3 className="font-semibold text-blue-900 mb-3 flex items-center gap-2">
          <AlertCircle className="w-5 h-5" />
          So erhalten Sie Ihre reCAPTCHA-Keys:
        </h3>
        <ol className="list-decimal list-inside space-y-2 text-blue-800 text-sm">
          <li>Gehen Sie zu <a href="https://www.google.com/recaptcha/admin" target="_blank" rel="noopener noreferrer" className="font-semibold underline hover:text-blue-600">Google reCAPTCHA Admin Console</a></li>
          <li>Melden Sie sich mit Ihrem Google-Konto an</li>
          <li>Klicken Sie auf "+" um eine neue Site zu registrieren</li>
          <li>Wählen Sie "reCAPTCHA v2" → "I'm not a robot" Checkbox</li>
          <li>Fügen Sie Ihre Domains hinzu (z.B. banv24.de, www.banv24.de)</li>
          <li>Kopieren Sie den <strong>Site Key</strong> und <strong>Secret Key</strong></li>
          <li>Fügen Sie die Keys unten ein und klicken Sie auf "Speichern"</li>
        </ol>
      </div>

      {/* Settings Form */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-semibold text-gray-800 mb-6">Konfiguration</h2>

        {/* Enable/Disable Toggle */}
        <div className="mb-6 p-4 bg-gray-50 rounded-lg">
          <label className="flex items-center justify-between cursor-pointer">
            <div className="flex items-center gap-3">
              <div className={`w-12 h-12 rounded-full flex items-center justify-center ${settings.enabled ? 'bg-green-100' : 'bg-gray-100'}`}>
                {settings.enabled ? (
                  <CheckCircle2 className="w-6 h-6 text-green-600" />
                ) : (
                  <AlertCircle className="w-6 h-6 text-gray-400" />
                )}
              </div>
              <div>
                <span className="font-semibold text-gray-800">reCAPTCHA aktivieren</span>
                <p className="text-sm text-gray-600">Schutz bei der Registrierung aktivieren/deaktivieren</p>
              </div>
            </div>
            <input
              type="checkbox"
              checked={settings.enabled}
              onChange={(e) => setSettings({ ...settings, enabled: e.target.checked })}
              className="w-12 h-6 bg-gray-300 rounded-full appearance-none cursor-pointer relative
                       checked:bg-green-500 transition-colors duration-200
                       before:content-[''] before:absolute before:w-5 before:h-5 before:bg-white 
                       before:rounded-full before:top-0.5 before:left-0.5 before:transition-transform 
                       before:duration-200 checked:before:translate-x-6"
            />
          </label>
        </div>

        {/* Site Key */}
        <div className="mb-6">
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Site Key (Öffentlich)
          </label>
          <input
            type="text"
            value={settings.site_key}
            onChange={(e) => setSettings({ ...settings, site_key: e.target.value })}
            placeholder="6Lc..."
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all font-mono text-sm"
          />
          <p className="text-xs text-gray-500 mt-1">
            Dieser Key wird im Frontend verwendet (sichtbar für Benutzer)
          </p>
        </div>

        {/* Secret Key */}
        <div className="mb-6">
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Secret Key (Privat)
          </label>
          <div className="relative">
            <input
              type={showSecretKey ? "text" : "password"}
              value={settings.secret_key}
              onChange={(e) => setSettings({ ...settings, secret_key: e.target.value })}
              placeholder="6Lc..."
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all font-mono text-sm pr-12"
            />
            <button
              type="button"
              onClick={() => setShowSecretKey(!showSecretKey)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 transition-colors"
            >
              {showSecretKey ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
            </button>
          </div>
          <p className="text-xs text-gray-500 mt-1">
            Dieser Key wird im Backend zur Verifizierung verwendet (streng geheim!)
          </p>
        </div>

        {/* Current Status */}
        <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
          <h3 className="font-semibold text-gray-800 mb-2">Aktueller Status</h3>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600">Status:</span>
              <span className={`font-semibold ${settings.enabled ? 'text-green-600' : 'text-red-600'}`}>
                {settings.enabled ? '✓ Aktiviert' : '✗ Deaktiviert'}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Site Key konfiguriert:</span>
              <span className={`font-semibold ${settings.site_key ? 'text-green-600' : 'text-red-600'}`}>
                {settings.site_key ? '✓ Ja' : '✗ Nein'}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Secret Key konfiguriert:</span>
              <span className={`font-semibold ${settings.secret_key ? 'text-green-600' : 'text-red-600'}`}>
                {settings.secret_key ? '✓ Ja' : '✗ Nein'}
              </span>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3">
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex-1 bg-blue-600 text-white py-3 px-6 rounded-lg font-semibold 
                     hover:bg-blue-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed
                     flex items-center justify-center gap-2"
          >
            {saving ? (
              <>
                <RefreshCw className="w-5 h-5 animate-spin" />
                Speichern...
              </>
            ) : (
              <>
                <Save className="w-5 h-5" />
                Einstellungen speichern
              </>
            )}
          </button>
          <button
            onClick={fetchSettings}
            disabled={saving}
            className="bg-gray-200 text-gray-700 py-3 px-6 rounded-lg font-semibold 
                     hover:bg-gray-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed
                     flex items-center justify-center gap-2"
          >
            <RefreshCw className="w-5 h-5" />
            Zurücksetzen
          </button>
        </div>
      </div>

      {/* Warning */}
      {!settings.enabled && (
        <div className="mt-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5" />
            <div>
              <h4 className="font-semibold text-yellow-900">reCAPTCHA ist deaktiviert</h4>
              <p className="text-sm text-yellow-800 mt-1">
                Die Registrierung ist derzeit nicht durch reCAPTCHA geschützt. Dies kann zu Spam-Registrierungen führen.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReCaptchaSettings;
